import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { PageSEO } from "@/components/PageSEO";
import { CaroselloStudio } from "@/components/CaroselloStudio";
import { SEO } from "@/lib/seo";
import { sendAdsConversion, isTestLead, ADS_LABELS } from "@/lib/adsConversions";
import { getLeadEventId, sendLeadEvent } from "@/lib/leadEvents";
import {
  API, SERVICES, PROBLEMS, SOLUTIONS, METHOD_STEPS,
  TESTIMONIALS_DATA, LOCATIONS, SOCIAL_LINKS, WHATSAPP_NUMBER,
  CONTACT_EMAIL, TEAM_ITEMS
} from "@/lib/constants";
import {
  FileCheck, Compass, BarChart3, Phone, Mail, MapPin,
  Facebook, Instagram, Linkedin, Menu, X, Star, ArrowRight,
  CheckCircle, Users, Clock, Award, ChevronRight, MessageCircle,
  Building, Target, TrendingUp, Shield, Play, XCircle, Zap,
  Eye, Lock, AlertTriangle, Sparkles, ArrowDown, Edit, Trash2,
  Plus, Save, LogOut, Image, Download, ShoppingCart, FileSpreadsheet,
  HelpCircle, ChevronDown, CircleDollarSign, CalendarCheck, PieChart,
  FileText, Wallet, BadgePercent, Sparkle, RefreshCw, ShieldCheck, BookOpen
} from "lucide-react";

const ContattiPage = () => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "", service: "" });
  const [consensoMarketing, setConsensoMarketing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Fire-and-forget push al CRM proprietario dello studio.
  // Non blocca il flusso: se il CRM è irraggiungibile, l'utente vede comunque il successo del form.
  // Attribuzione presa dal cookie sc_attr di prima parte (attribution.js), first-touch persistente.
  const sendToCrm = () => {
    try {
      const attr = (typeof window !== 'undefined' && window.__attribution) ? window.__attribution : {};
      const body = {
        nome: formData.name || "",
        email: formData.email || "",
        telefono: formData.phone || "",
        azienda: "",
        messaggio: formData.message || "",
        consenso_marketing: !!consensoMarketing,
        provenienza: {
          utm_source: attr.utm_source || "",
          utm_medium: attr.utm_medium || "",
          utm_campaign: attr.utm_campaign || "",
          gclid: attr.gclid || "",
          gbraid: attr.gbraid || "",
          wbraid: attr.wbraid || "",
        },
      };
      fetch("https://crm.casalisilvia.com/api/contatti?k=79b005b5089105ac6ca251c65c6132ba", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        body: JSON.stringify(body),
      }).catch(() => {});
    } catch (e) { /* fire-and-forget */ }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Push al CRM prima del POST principale: keepalive fa partire la richiesta anche
    // se lo state successivo triggera un re-render pesante.
    sendToCrm();
    try {
      const leadEventId = getLeadEventId();
      await axios.post(`${API}/contact`, { ...formData, consenso_marketing: consensoMarketing });
      if (!isTestLead(formData.name, formData.email)) {
        sendAdsConversion(ADS_LABELS.contatto, { value: 50.0 });
        sendLeadEvent(leadEventId);
      }
      setIsSubmitted(true);
      toast.success("Messaggio inviato! Ti ricontatteremo presto.");
      setFormData({ name: "", email: "", phone: "", message: "", service: "" });
      setConsensoMarketing(false);
    } catch (error) {
      toast.error("Errore nell'invio. Riprova o scrivici su WhatsApp.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="pt-20" data-testid="contatti-page">
      <PageSEO {...SEO.contatti} />
      <section className="py-24 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <p className="text-gold font-bold tracking-widest text-sm mb-4">INIZIAMO</p>
              <h1 className="font-heading text-6xl md:text-7xl text-[#0D1527] leading-none mb-6">
                FACCIAMOCI<br /><span className="text-gold">UNA CHIACCHIERATA</span>
              </h1>
              <p className="text-xl text-[#4A5568] mb-6">
                Niente moduli complicati. Scrivici due righe, ti ricontattiamo noi 
                e capiamo insieme qual e il servizio giusto per te.
              </p>
              
              <div className="mb-8">
                <CaroselloStudio testid="contatti-carosello-studio" />
              </div>

              {/* Come funziona */}
              <div className="bg-white border border-[#E8E2D5] rounded-lg p-6 mb-8">
                <h3 className="font-heading text-xl text-[#0D1527] mb-4">COME FUNZIONA?</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-gold rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-black font-bold text-sm">1</span>
                    </div>
                    <div>
                      <p className="text-[#0D1527] font-medium">Tu ci scrivi</p>
                      <p className="text-[#64708A] text-sm">Anche solo "vorrei capirne di piu" va benissimo</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-gold rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-black font-bold text-sm">2</span>
                    </div>
                    <div>
                      <p className="text-[#0D1527] font-medium">Noi ti ricontattiamo</p>
                      <p className="text-[#64708A] text-sm">Ti facciamo qualche domanda per capire la tua situazione</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-gold rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-black font-bold text-sm">3</span>
                    </div>
                    <div>
                      <p className="text-[#0D1527] font-medium">Troviamo il servizio giusto</p>
                      <p className="text-[#64708A] text-sm">Ti proponiamo il servizio più adatto alle tue esigenze, senza impegno</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-gold rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-black font-bold text-sm">4</span>
                    </div>
                    <div>
                      <p className="text-[#0D1527] font-medium">Partiamo subito</p>
                      <p className="text-[#64708A] text-sm">Niente burocrazia infinita: si inizia in modo semplice e veloce</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4 mb-8">
                <a href={`https://wa.me/${WHATSAPP_NUMBER}?text=Ciao, vorrei capire quale servizio fa al caso mio.`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-4 bg-[#25D366]/10 border border-[#25D366]/30 rounded-lg hover:bg-[#25D366]/20 transition-all group" data-testid="contact-whatsapp">
                  <div className="w-12 h-12 bg-[#25D366] rounded flex items-center justify-center">
                    <MessageCircle className="text-[#0D1527]" size={24} />
                  </div>
                  <div>
                    <p className="text-[#0D1527] font-medium">Preferisci WhatsApp?</p>
                    <p className="text-[#4A5568] text-sm">Scrivici direttamente, ti ricontattiamo noi</p>
                  </div>
                </a>

                <a href={`mailto:${CONTACT_EMAIL}`} className="flex items-center gap-4 p-4 bg-white border border-[#E8E2D5] rounded-lg hover:border-gold/50 transition-all group" data-testid="contact-email">
                  <div className="w-12 h-12 bg-[#F7F0E1] rounded flex items-center justify-center">
                    <Mail className="text-gold" size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-[#64708A]">Oppure via email</p>
                    <p className="text-[#0D1527] font-medium">{CONTACT_EMAIL}</p>
                  </div>
                </a>
              </div>

              <div className="flex items-start gap-4 p-4 bg-white border border-[#E8E2D5] rounded-lg">
                <MapPin className="text-gold flex-shrink-0" size={20} />
                <div>
                  <p className="text-[#3B4658] text-sm">Sede Legale: Via Ponte Marino 41, Ravenna</p>
                  <p className="text-[#64708A] text-sm">Altre sedi: {LOCATIONS.others.join(", ")}</p>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="bg-white border border-[#E8E2D5] p-8 md:p-10 rounded-lg">
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="text-gold" size={40} />
                  </div>
                  <h3 className="font-heading text-3xl text-[#0D1527] mb-4">PERFETTO!</h3>
                  <p className="text-[#4A5568] mb-2">Abbiamo ricevuto il tuo messaggio.</p>
                  <p className="text-[#64708A] mb-6">Ti ricontattiamo noi per capire insieme qual e il servizio giusto per te.</p>
                  <button onClick={() => setIsSubmitted(false)} className="text-gold font-medium hover:underline">
                    Invia un altro messaggio
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form">
                  <h2 className="font-heading text-3xl text-[#0D1527] mb-2">SCRIVICI DUE RIGHE</h2>
                  <p className="text-[#64708A] text-sm mb-8">Pensiamo a tutto noi. Tu dicci solo come contattarti.</p>
                  
                  <div className="space-y-6">
                    <div>
                      <label htmlFor="name" className="block text-sm text-[#4A5568] mb-2">Come ti chiami? *</label>
                      <input
                        type="text"
                        id="name"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full bg-white border border-[#D6CFC0] focus:border-gold rounded px-4 py-3 text-[#0D1527] placeholder:text-[#98A0B0] outline-none transition-colors"
                        placeholder="Il tuo nome"
                        data-testid="input-name"
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="email" className="block text-sm text-[#4A5568] mb-2">Email *</label>
                        <input
                          type="email"
                          id="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="w-full bg-white border border-[#D6CFC0] focus:border-gold rounded px-4 py-3 text-[#0D1527] placeholder:text-[#98A0B0] outline-none transition-colors"
                          data-testid="input-email"
                        />
                      </div>
                      <div>
                        <label htmlFor="phone" className="block text-sm text-[#4A5568] mb-2">Telefono</label>
                        <input
                          type="tel"
                          id="phone"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="w-full bg-white border border-[#D6CFC0] focus:border-gold rounded px-4 py-3 text-[#0D1527] placeholder:text-[#98A0B0] outline-none transition-colors"
                          placeholder="Per richiamarti"
                          data-testid="input-phone"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="service" className="block text-sm text-[#4A5568] mb-2">Cosa ti interessa? (opzionale)</label>
                      <select
                        id="service"
                        value={formData.service}
                        onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                        className="w-full bg-white border border-[#D6CFC0] focus:border-gold rounded px-4 py-3 text-[#0D1527] outline-none transition-colors"
                        data-testid="input-service"
                      >
                        <option value="">Non so ancora, vorrei capire</option>
                        <option value="adempimenti">Adempimenti (contabilita, F24)</option>
                        <option value="controllo">Controllo periodico dei numeri</option>
                        <option value="consulenza">Consulenza strategica</option>
                        <option value="pannello">Pannello Direzionale</option>
                        <option value="altro">Altro</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm text-[#4A5568] mb-2">Raccontaci brevemente (opzionale)</label>
                      <textarea
                        id="message"
                        rows={3}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className="w-full bg-white border border-[#D6CFC0] focus:border-gold rounded px-4 py-3 text-[#0D1527] placeholder:text-[#98A0B0] outline-none transition-colors resize-none"
                        placeholder="Es: ho una SRL e vorrei capire meglio i miei numeri..."
                        data-testid="input-message"
                      />
                    </div>

                    <label className="flex items-start gap-2 cursor-pointer select-none" data-testid="contact-newsletter-label">
                      <input
                        type="checkbox"
                        checked={consensoMarketing}
                        onChange={(e) => setConsensoMarketing(e.target.checked)}
                        className="mt-1 accent-gold flex-shrink-0"
                        data-testid="contact-newsletter-checkbox"
                      />
                      <span className="text-[#4A5568] text-xs leading-snug">
                        Desidero ricevere anche la newsletter dello Studio con novità fiscali e scadenze. Potrò disiscrivermi in ogni momento.
                      </span>
                    </label>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-gold text-black hover:bg-[#96742F] py-4 rounded font-bold tracking-wide transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                      data-testid="submit-button"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                          INVIO IN CORSO...
                        </>
                      ) : (
                        <>CONTATTACI <ArrowRight size={18} /></>
                      )}
                    </button>

                    <p className="text-[#8B94A6] text-xs text-center">
                      Nessun impegno. Ti ricontattiamo per capire insieme cosa ti serve.
                    </p>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Social */}
      <section className="py-16 bg-[#F4EFEA]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <p className="text-[#64708A] mb-6">Seguici anche sui social</p>
          <div className="flex justify-center gap-4">
            {[
              { href: SOCIAL_LINKS.facebook, icon: Facebook },
              { href: SOCIAL_LINKS.instagram, icon: Instagram },
              { href: SOCIAL_LINKS.linkedin, icon: Linkedin }
            ].map((s, i) => (
              <a key={i} href={s.href} target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-[#EDE6DA] rounded flex items-center justify-center hover:bg-gold hover:text-black transition-all">
                <s.icon size={20} />
              </a>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

// Newsletter Form Component

export { ContattiPage };
